import React, { useState, useRef, useEffect } from "react";
import { Plus, Pencil, X, Search, Check } from "lucide-react";
import api, { money } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const num = (v) => { const n = Number(v); return isNaN(n) ? 0 : n; };
const numOk = (v) => v === "" || /^\d*\.?\d*$/.test(v);

// Editor de ítems: un formulario fijo de captura arriba + lista de ítems agregados abajo.
// Al presionar Enter en el último campo (o "Añadir") agrega el ítem a la lista y limpia el
// formulario para el siguiente. Cada ítem de la lista puede editarse o eliminarse.
export const ItemsEditor = ({
  items = [], onChange, currency = "GTQ",
  priceKey = "unit_price", priceLabel = "Precio",
  showDiscount = true, showObservation = true, showInternalCode = false,
  productMap = null, testidPrefix = "item",
}) => {
  const blank = {
    product_id: "", code: "", internal_code: "", description: "", unit: "Unidad",
    qty: "", [priceKey]: "", discount_pct: "", observation: "",
  };
  const [draft, setDraft] = useState(blank);
  const [editIdx, setEditIdx] = useState(null);
  const [prodSearch, setProdSearch] = useState("");
  const [prodResults, setProdResults] = useState([]);
  const refs = useRef({});

  const fieldOrder = ["code", showInternalCode && "internal_code", "qty", "description", priceKey,
    showDiscount && "discount_pct", showObservation && "observation"].filter(Boolean);

  useEffect(() => {
    if (!productMap || !prodSearch) { setProdResults([]); return; }
    const t = setTimeout(() => api.get(`/products?q=${encodeURIComponent(prodSearch)}`)
      .then((r) => setProdResults(r.data.slice(0, 8))).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [prodSearch, productMap]);

  const lineTotal = (it) => {
    const base = num(it.qty) * num(it[priceKey]);
    return showDiscount ? base * (1 - num(it.discount_pct) / 100) : base;
  };

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const commit = () => {
    if (!String(draft.description).trim() && !String(draft.code).trim()) return;
    const clean = {
      ...draft, qty: num(draft.qty) || (editIdx === null ? 1 : 0),
      [priceKey]: num(draft[priceKey]), discount_pct: num(draft.discount_pct),
    };
    onChange(editIdx !== null ? items.map((it, i) => (i === editIdx ? clean : it)) : [...items, clean]);
    setDraft(blank); setEditIdx(null);
    setTimeout(() => refs.current[fieldOrder[0]]?.focus(), 0);
  };

  const onKey = (e, key) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const idx = fieldOrder.indexOf(key);
    if (idx < fieldOrder.length - 1) refs.current[fieldOrder[idx + 1]]?.focus();
    else commit();
  };

  const editRow = (i) => {
    const it = items[i];
    setDraft({
      ...blank, ...it, qty: String(it.qty ?? ""), [priceKey]: String(it[priceKey] ?? ""),
      discount_pct: String(it.discount_pct ?? ""),
    });
    setEditIdx(i);
    setTimeout(() => refs.current[fieldOrder[0]]?.focus(), 0);
  };
  const rmRow = (i) => { onChange(items.filter((_, x) => x !== i)); if (editIdx === i) { setDraft(blank); setEditIdx(null); } };
  const cancelEdit = () => { setDraft(blank); setEditIdx(null); };
  const addProduct = (p) => { onChange([...items, { ...blank, ...productMap(p), qty: 1, discount_pct: 0 }]); setProdSearch(""); setProdResults([]); };

  const numInput = "rounded-sm h-10 text-sm text-right";
  const reg = (key) => (el) => { refs.current[key] = el; };

  return (
    <div className="space-y-3">
      {/* Buscador de productos (agrega directo a la lista) */}
      {productMap && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={prodSearch} onChange={(e) => setProdSearch(e.target.value)}
            placeholder="Buscar producto por código o descripción..." className="pl-9 rounded-sm"
            data-testid={`${testidPrefix}-product-search`} />
          {prodResults.length > 0 && (
            <div className="absolute z-30 mt-1 w-full bg-popover border border-border rounded-sm shadow-lg max-h-56 overflow-y-auto">
              {prodResults.map((p) => (
                <button key={p.id} type="button" onClick={() => addProduct(p)}
                  className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between"
                  data-testid={`${testidPrefix}-prod-opt-${p.code}`}>
                  <span><span className="font-mono text-xs text-muted-foreground">{p.code}</span> · {p.description}</span>
                  <span className="font-mono text-xs">{money(p.currency || currency, p.price ?? p.cost ?? 0)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Formulario fijo de captura */}
      <div className="border border-border rounded-sm p-3 bg-muted/30 space-y-2" data-testid={`${testidPrefix}-entry-form`}>
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-28 space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Código</Label>
            <Input ref={reg("code")} className="rounded-sm h-10 text-sm" value={draft.code}
              onChange={(e) => set("code", e.target.value)} onKeyDown={(e) => onKey(e, "code")}
              data-testid={`${testidPrefix}-draft-code`} />
          </div>
          {showInternalCode && (
            <div className="w-28 space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Cód. interno</Label>
              <Input ref={reg("internal_code")} className="rounded-sm h-10 text-sm" value={draft.internal_code}
                onChange={(e) => set("internal_code", e.target.value)} onKeyDown={(e) => onKey(e, "internal_code")}
                data-testid={`${testidPrefix}-draft-internal`} />
            </div>
          )}
          <div className="w-24 space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Cantidad</Label>
            <Input ref={reg("qty")} type="text" inputMode="decimal" className={numInput} value={draft.qty}
              onChange={(e) => numOk(e.target.value) && set("qty", e.target.value)} onKeyDown={(e) => onKey(e, "qty")}
              placeholder="0" data-testid={`${testidPrefix}-draft-qty`} />
          </div>
          <div className="flex-1 min-w-[200px] space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Descripción</Label>
            <Input ref={reg("description")} className="rounded-sm h-10 text-sm w-full" value={draft.description}
              onChange={(e) => set("description", e.target.value)} onKeyDown={(e) => onKey(e, "description")}
              placeholder="Descripción del producto/servicio" data-testid={`${testidPrefix}-draft-desc`} />
          </div>
          <div className="w-28 space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{priceLabel}</Label>
            <Input ref={reg(priceKey)} type="text" inputMode="decimal" className={numInput} value={draft[priceKey]}
              onChange={(e) => numOk(e.target.value) && set(priceKey, e.target.value)} onKeyDown={(e) => onKey(e, priceKey)}
              placeholder="0" data-testid={`${testidPrefix}-draft-price`} />
          </div>
          {showDiscount && (
            <div className="w-20 space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Desc.%</Label>
              <Input ref={reg("discount_pct")} type="text" inputMode="decimal" className={numInput} value={draft.discount_pct}
                onChange={(e) => numOk(e.target.value) && set("discount_pct", e.target.value)} onKeyDown={(e) => onKey(e, "discount_pct")}
                placeholder="0" data-testid={`${testidPrefix}-draft-disc`} />
            </div>
          )}
          <div className="flex items-end gap-1">
            <Button type="button" className="rounded-sm h-10 gap-1 font-bold" onClick={commit} data-testid={`${testidPrefix}-add-btn`}>
              {editIdx !== null ? <><Check className="h-4 w-4" /> Actualizar</> : <><Plus className="h-4 w-4" /> Añadir</>}
            </Button>
            {editIdx !== null && (
              <Button type="button" variant="ghost" className="rounded-sm h-10 px-2" onClick={cancelEdit} data-testid={`${testidPrefix}-cancel-edit`}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        {showObservation && (
          <Input ref={reg("observation")} className="rounded-sm h-9 text-xs w-full" value={draft.observation}
            onChange={(e) => set("observation", e.target.value)} onKeyDown={(e) => onKey(e, "observation")}
            placeholder="Observación de la línea (opcional)" data-testid={`${testidPrefix}-draft-obs`} />
        )}
      </div>

      {/* Lista de ítems agregados */}
      <div className="border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${testidPrefix}-list`} data-lineitems>
          <thead><tr className="bg-muted text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 w-28">Código</th><th className="px-3 py-2 w-16 text-right">Cant.</th>
            <th className="px-3 py-2">Descripción</th><th className="px-3 py-2 w-24 text-right">{priceLabel}</th>
            {showDiscount && <th className="px-3 py-2 w-16 text-right">Desc.%</th>}
            <th className="px-3 py-2 w-24 text-right">Total</th><th className="px-3 py-2 w-16"></th></tr></thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={showDiscount ? 7 : 6} className="text-center py-6 text-muted-foreground text-sm">
                Sin ítems. Captura arriba y presiona Enter o "Añadir".</td></tr>
            )}
            {items.map((it, idx) => (
              <tr key={idx} className={`border-t border-border ${editIdx === idx ? "bg-primary/10" : "hover:bg-muted/40"}`} data-testid={`${testidPrefix}-row-${idx}`}>
                <td className="px-3 py-2 font-mono text-xs">{it.code || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">{num(it.qty)}</td>
                <td className="px-3 py-2">
                  <div>{it.description || "—"}</div>
                  {it.observation ? <div className="text-[11px] text-muted-foreground italic">{it.observation}</div> : null}
                </td>
                <td className="px-3 py-2 text-right font-mono">{money(currency, num(it[priceKey]))}</td>
                {showDiscount && <td className="px-3 py-2 text-right font-mono">{num(it.discount_pct)}%</td>}
                <td className="px-3 py-2 text-right font-mono font-semibold">{money(currency, lineTotal(it))}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button type="button" onClick={() => editRow(idx)} className="text-blue-600 p-1" title="Editar" data-testid={`${testidPrefix}-edit-${idx}`}><Pencil className="h-4 w-4" /></button>
                  <button type="button" onClick={() => rmRow(idx)} className="text-destructive p-1" title="Eliminar" data-testid={`${testidPrefix}-rm-${idx}`}><X className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
